import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import BotonVolver from "../../../components/BotonVolver";

const AdminGestionProductos = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importando, setImportando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProductos();
  }, []);

  const fetchProductos = async () => {
    try {
      const res = await axios.get(`/api/productos`);
      setProductos(res.data);
    } catch (err) {
      setError("❌ Error al cargar productos.");
    } finally {
      setLoading(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    try {
      await axios.delete(`/api/productos/${id}`);
      fetchProductos();
      alert("✅ Producto eliminado correctamente");
    } catch {
      alert("❌ No se pudo eliminar el producto.");
    }
  };

  const handleImportar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setImportando(true);
    try {
      const resp = await axios.post(`/api/productos/importar`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = resp?.data || {};
      let msg = `✅ Productos importados correctamente`;
      if (typeof data.total_importados === 'number') {
        msg += `\nImportados: ${data.total_importados}`;
      }
      if (typeof data.duplicados_omitidos === 'number' && data.duplicados_omitidos > 0) {
        msg += `\nDuplicados omitidos: ${data.duplicados_omitidos}`;
        if (Array.isArray(data.ids_duplicados) && data.ids_duplicados.length) {
          msg += `\nIDs duplicados: ${data.ids_duplicados.join(', ')}`;
        }
      }
      alert(msg);
      fetchProductos();
      e.target.value = null;
    } catch (error) {
      const dataErr = error.response?.data;
      let msg = `❌ Error al importar: ${dataErr?.error || error.message}`;
      if (Array.isArray(dataErr?.filas_afectadas) && dataErr.filas_afectadas.length) {
        msg += `\nFilas afectadas: ${dataErr.filas_afectadas.join(", ")}`;
      }
      alert(msg);
    } finally {
      setImportando(false);
    }
  };

  const handleExportar = async () => {
    const confirmar = window.confirm("¿Deseas exportar los productos a Excel?");
    if (!confirmar) return;

    try {
      const response = await axios.get(`/api/productos/exportar`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "productos_descargados_app.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      alert("✅ Excel exportado correctamente.");
    } catch (error) {
      alert("❌ Error al exportar: " + (error.response?.data?.error || error.message));
    }
  };

  const handleAnadir = () => {
    navigate("/productos/nuevo");
  };

  // Filtrar productos según busqueda (por nombre, case-insensitive)
  const normalizar = (str) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const productosFiltrados = productos
    .map((cat) => ({
      ...cat,
      subcategorias: cat.subcategorias
        .map((sub) => ({
          ...sub,
          productos: sub.productos.filter((p) =>
            normalizar(p.nombre).includes(normalizar(busqueda))
          ),
        }))
        .filter((sub) => sub.productos.length > 0),
    }))
    .filter((cat) => cat.subcategorias.length > 0);


  return (
    <div
      className="admin-gestion-productos-container"
      style={{ position: "relative", minHeight: "100vh", padding: "20px" }}
    >
      <BotonVolver fallback="/admin" />
      <h2 style={styles.title}>Gestión de Productos</h2>

      {/* Input de búsqueda */}
      <input
        type="text"
        placeholder="Buscar producto por nombre..."
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        style={styles.searchInput}
      />

      <input
        type="file"
        accept=".xlsx, .xls"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleImportar}
      />

      <button
        onClick={() => fileInputRef.current?.click()}
        style={{
          position: "fixed",
          top: "20px",
          right: "20px",
          zIndex: 1000,
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        }}
        disabled={importando}
      >
        📥 Importar Excel
      </button>

      <button
        onClick={handleExportar}
        style={{
          position: "fixed",
          top: "20px",
          left: "20px",
          zIndex: 1000,
          padding: "10px 20px",
          backgroundColor: "#17a2b8",
          color: "white",
          border: "none",
          borderRadius: "5px",
          cursor: "pointer",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
        }}
        disabled={importando}
      >
        📤 Exportar Excel
      </button>

      {importando && (
        <div
          style={{ textAlign: "center", marginTop: "20px", fontSize: "16px", color: "#007bff" }}
        >
          ⏳ Importando...
        </div>
      )}

      {loading ? (
        <p style={{ textAlign: "center", marginTop: "30px" }}>⏳ Cargando productos...</p>
      ) : error ? (
        <p style={{ color: "red", textAlign: "center" }}>{error}</p>
      ) : productosFiltrados.length === 0 ? (
        <p style={{ textAlign: "center", marginTop: "30px" }}>
          No se encontraron productos que coincidan con la búsqueda.
        </p>
      ) : (
        <>
          {productosFiltrados
            .slice() // hacemos copia para no mutar el original
            .sort((a, b) => (a.categoria === "Premium" ? 1 : b.categoria === "Premium" ? -1 : 0)) // 'Premium' al final
            .map((cat) => (
              <details
                key={cat.categoria}
                className="category"
                style={{
                  ...styles.category,
                  backgroundColor: cat.categoria === "Premium" ? "#fff176" : styles.category.backgroundColor, // amarillo claro
                }}
                open={busqueda.length > 0}
              >
                <summary style={styles.categoryTitle}>{cat.categoria}</summary>
                {cat.subcategorias.map((sub) => (
                  <details
                    key={sub.subcategoria}
                    className="subcategory"
                    style={{
                      ...styles.subcategory,
                      backgroundColor: cat.categoria === "Premium" ? "#fff59d" : styles.subcategory.backgroundColor, // un poco más claro
                    }}
                    open={busqueda.length > 0}
                  >
                    <summary style={styles.subcategoryTitle}>{sub.subcategoria}</summary>
                    {sub.productos.map((p) => (
                      <div key={p.id} className="producto-row">
                        <img src={p.imagen} alt={p.nombre} className="producto-imagen" />
                        <div className="producto-info">
                          <strong>{p.nombre}</strong>
                          <div>Cantidad: {p.cantidad}kg</div>
                          <div style={styles.price}>
                            Precio: {p.precio} €{" "}
                            <span style={styles.precioKg}>({p["precio/kg"]} €/kg)</span>
                          </div>
                        </div>
                        <div className="producto-actions">
                          <button
                            style={styles.edit}
                            onClick={() => navigate(`/productos/editar/${p.id}`)}
                            disabled={importando}
                          >
                            ✏️ Editar
                          </button>
                          <button
                            style={styles.delete}
                            onClick={() => handleEliminar(p.id)}
                            disabled={importando}
                          >
                            🗑 Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </details>
                ))}
              </details>
            ))}

        </>
      )}

      <button
        onClick={handleAnadir}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          zIndex: 1000,
          padding: "15px 25px",
          backgroundColor: "#28a745",
          color: "white",
          border: "none",
          borderRadius: "50px",
          fontSize: "18px",
          fontWeight: "bold",
          cursor: "pointer",
          boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
        }}
        title="Añadir Producto"
        disabled={importando}
      >
        ➕ Añadir Producto
      </button>
    </div>
  );
};

const styles = {
  title: {
    fontSize: "32px",
    marginBottom: "30px",
    fontWeight: "bold",
    textAlign: "center",
  },
  searchInput: {
    display: "block",
    margin: "0 auto 30px auto",
    padding: "10px 15px",
    fontSize: "16px",
    width: "320px",
    maxWidth: "90%",
    borderRadius: "6px",
    border: "1px solid #ccc",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
    outline: "none",
    transition: "border-color 0.3s ease",
    // Al enfocar cambia el borde
    ":focus": {
      borderColor: "#007bff",
    },
  },
  category: {
    marginBottom: "30px",
    borderTop: "2px solid #ccc",
    borderBottom: "2px solid #ccc",
    padding: "20px",
    backgroundColor: "#d9fdd3",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    textAlign: "center",
  },
  categoryTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    cursor: "pointer",
    marginBottom: "15px",
  },
  subcategory: {
    margin: "20px 0",
    padding: "20px",
    backgroundColor: "#eefdea",
    borderTop: "1px solid #bbb",
    borderBottom: "1px solid #bbb",
    boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)",
  },
  subcategoryTitle: {
    fontSize: "20px",
    fontWeight: "bold",
    marginBottom: "15px",
    cursor: "pointer",
  },
  price: {
    fontWeight: "bold",
    marginTop: "8px",
  },
  precioKg: {
    fontSize: "14px",
    color: "#666",
    marginLeft: "5px",
  },
  edit: {
    padding: "8px 14px",
    backgroundColor: "#ffc107",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
  delete: {
    padding: "8px 14px",
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "14px",
  },
};

export default AdminGestionProductos;
